/**
 * AssignmentAddPopover — "+ Add assignment" for a SET list, in two steps:
 *   1. a column picker (`DmlColumnPicker`) over the written table's columns —
 *      columns the list already sets are check-marked and not pickable, and a
 *      typed name the table doesn't list can be used as-is;
 *   2. an inline draft row `column = [value] ✓ ×` (`AssignmentDraftRow`) that
 *      takes the trigger's place at the end of the list until applied or
 *      cancelled.
 *
 * Routing seams (checked in order when the draft is applied):
 *   - `onSubmitAssignment` — full override; the caller composes its own commit
 *     from the target/value pair. Used by the ON CONFLICT action switch to
 *     build a DO UPDATE seed (`modify-insert-conflict-action`) out of THIS flow
 *     instead of hand-building a second one (§2.4 S6b, decision 9).
 *   - `mergeBranch` — `add-merge-update-assignment` on the picked MERGE branch.
 *   - `conflictTail` — `add-insert-conflict-assignment` on an INSERT structured
 *     tail (ON CONFLICT / ON DUPLICATE) — the third router (§2.4 S6b).
 * `mergeBranch` / `conflictTail` also pick whose columns the picker lists.
 *
 * Callers mount it only with an edit context (SetAssignmentsList renders its
 * own disabled `[+ add assignment]` scaffold without one).
 *
 * PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits.
 */

import { useRef, useState } from 'react';

import type { Column } from '../../../../sql-resolver/types/schema';
import type { ConflictTailTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { addUpdateAssignment } from '../../edit/edits/addUpdateAssignment';
import { formatIdentifierPart } from '../../edit/edits/changeDmlTarget';
import { addInsertConflictAssignment } from '../../edit/edits/insertConflictAssignments';
import { addMergeUpdateAssignment, type MergeBranchSelector } from '../../edit/edits/modifyMergeBranch';
import { recordEditFailure, safeExtractStructure } from '../insert/insertColumnMapHelpers';
import { vqeCopy } from '../../ux/vqeCopy';
import { resolveWrittenTableColumns, sharedQualifier, tableForRef } from './assignmentColumns';
import { AssignmentDraftRow } from './AssignmentDraftRow';
import { DmlColumnPicker } from './DmlColumnPicker';

export interface AssignmentAddPopoverProps {
  readonly editContext: VqeEditContext;
  readonly testIdPrefix: string;
  /**
   * When supplied, the add commit routes through `add-merge-update-assignment`
   * targeting the picked MERGE branch's nested SET list instead of the
   * top-level UPDATE statement.
   */
  readonly mergeBranch?: MergeBranchSelector;
  /** INSERT structured-tail router — see the module header. */
  readonly conflictTail?: ConflictTailTarget;
  /** Full submit override — see the module header. Wins over both routers. */
  readonly onSubmitAssignment?: (target: string, valueText: string) => Promise<void>;
  /** The list's current targets as written — the picker marks them as already set. */
  readonly existingTargets?: readonly string[];
  /**
   * Open the column picker as soon as it mounts. The ON CONFLICT seed flow
   * mounts this fresh when the action switch picks DO UPDATE, so `defaultOpen`
   * gives it the "form appears immediately" behavior without controlled state.
   */
  readonly defaultOpen?: boolean;
  /** Fires true when the flow opens, false once it ends (applied, cancelled or dismissed). */
  readonly onOpenChange?: (open: boolean) => void;
}

type Draft = { readonly text: string; readonly column: Column | null };

export function AssignmentAddPopover({
  editContext,
  testIdPrefix,
  mergeBranch,
  conflictTail,
  onSubmitAssignment,
  existingTargets = [],
  defaultOpen,
  onOpenChange,
}: AssignmentAddPopoverProps) {
  const [draft, setDraft] = useState<Draft | null>(null);
  // Set by a pick so the picker's own close (closeOnSelect) doesn't end the flow.
  const pickedRef = useRef(false);
  const engine = editContext.service.engineId;
  const qualifier = sharedQualifier(existingTargets);
  // Targets all qualified by another table (MySQL `… JOIN b SET b.x = …`): that table's columns are unknown.
  const table = tableForRef(
    resolveWrittenTableColumns(editContext, { mergeBranch, conflictTail }),
    qualifier ? `${qualifier}.x` : 'x',
  );

  const finish = (): void => {
    setDraft(null);
    onOpenChange?.(false);
  };

  // The draft closes once the edit landed; a refused one keeps it (the
  // commit-feedback status says why) so the value can be fixed.
  const apply = async (valueText: string): Promise<void> => {
    if (!draft) return;
    const target = draft.text;
    const ctx = toCommitCtx(editContext);
    const outcome = await (onSubmitAssignment
      ? onSubmitAssignment(target, valueText)
      : mergeBranch
        ? addMergeUpdateAssignment(mergeBranch, target, valueText, ctx)
        : conflictTail
          ? addInsertConflictAssignment(target, valueText, conflictTail, ctx, {
              structure: safeExtractStructure(editContext),
            })
          : addUpdateAssignment(target, valueText, ctx)
    ).catch((err: unknown) => {
      recordEditFailure(err);
      return { status: 'failed' as const };
    });
    if (outcome?.status !== 'failed') finish();
  };

  if (draft) {
    const quickValues: string[] = [];
    if (draft.column?.nullable) quickValues.push('NULL');
    if (draft.column && conflictTail === 'on-conflict') {
      quickValues.push(`EXCLUDED.${formatIdentifierPart(draft.column.qualified.name, engine)}`);
    }
    return (
      <AssignmentDraftRow
        testIdPrefix={testIdPrefix}
        columnLabel={draft.text}
        column={draft.column}
        table={table}
        quickValues={quickValues}
        onApply={apply}
        onCancel={finish}
      />
    );
  }

  return (
    <DmlColumnPicker
      testId={`${testIdPrefix}-add-assignment`}
      table={table}
      takenRefs={existingTargets}
      qualifier={qualifier}
      usage="set"
      triggerLabel={vqeCopy.actions.addAssignment}
      defaultOpen={defaultOpen}
      onOpenChange={(open) => {
        if (open) {
          pickedRef.current = false;
          onOpenChange?.(true);
        } else if (!pickedRef.current) {
          onOpenChange?.(false);
        }
      }}
      onPick={({ column, text }) => {
        pickedRef.current = true;
        setDraft({ text, column });
      }}
    />
  );
}
